import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DefaultSocketMessageEventType } from "./interface";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";

export enum SUBSCRIPTION_WS_STATUS {
  REQUESTED = "REQUESTED",
  ACTIVATED = "ACTIVATED",
  CONFIRMED = "CONFIRMED",
  GENERATED = "GENERATED",
  REGISTERED = "REGISTERED",
}

export enum SUSPENSION_STATUS {
  ENABLED = "ENABLE",
  SUSPENDED = "SUSPEND",
  DISABLED = "DISABLE",
}

export enum PLAN_KEY_TYPE {
  AUTO_ENROLL = "autoEnroll",
  PERIOD = "period",
  STORAGE = "storage",
}

interface SubscriptionBaseEntity {
  event: DefaultSocketMessageEventType;
  bell: boolean;
  accountId: string;
  status: SUBSCRIPTION_WS_STATUS;
}

export interface SubscriptionWsEntity extends SubscriptionBaseEntity {
  username?: string;
  installer?: string;
  targetAccountId?: string;
}

export interface PlanWsEntity extends SubscriptionBaseEntity {
  keyType: PLAN_KEY_TYPE;
  targetAccountId?: string;
  paymentCycle?: BILLING_CYCLE;
}

export interface SubscriptionUpdateInterface {
  updatedDate: Date | null;
  accountId: string;
}

interface SubscriptionUpdateRequestInterface
  extends SubscriptionUpdateInterface {
  username: string;
  installer: string;
  targetAccountId: string;
}

interface PlanUpdateRequestInterface extends SubscriptionUpdateInterface {
  targetAccountId: string;
}

interface PlanUpdatePeriodRequestInterface extends SubscriptionUpdateInterface {
  paymentCycle?: BILLING_CYCLE;
}

interface StompState {
  subscriptionRequestUpdated: SubscriptionUpdateRequestInterface;
  subscriptionActivatedUpdated: SubscriptionUpdateInterface;
  subscriptionConfirmUpdated: SubscriptionUpdateInterface;
  subscriptionGenerateUpdated: SubscriptionUpdateInterface;
  subscriptionRegisterUpdated: SubscriptionUpdateInterface;

  planAutoEnrollUpdated: PlanUpdateRequestInterface;
  planPeriodUpdated: PlanUpdatePeriodRequestInterface;
  planStorageUpdated: PlanUpdateRequestInterface;
}

/**
 * enum → StompState 키 매핑
 */
export const SUBSCRIPTION_WS_TO_STATE_KEY: Record<
  SUBSCRIPTION_WS_STATUS,
  keyof StompState
> = {
  [SUBSCRIPTION_WS_STATUS.REQUESTED]: "subscriptionRequestUpdated",
  [SUBSCRIPTION_WS_STATUS.ACTIVATED]: "subscriptionActivatedUpdated",
  [SUBSCRIPTION_WS_STATUS.CONFIRMED]: "subscriptionConfirmUpdated",
  [SUBSCRIPTION_WS_STATUS.GENERATED]: "subscriptionGenerateUpdated",
  [SUBSCRIPTION_WS_STATUS.REGISTERED]: "subscriptionRegisterUpdated",
};

const createBaseUpdate = (): SubscriptionUpdateInterface => ({
  updatedDate: null,
  accountId: "",
});

const createRequestUpdate = (): SubscriptionUpdateRequestInterface => ({
  ...createBaseUpdate(),
  username: "",
  installer: "",
  targetAccountId: "",
});

const createPlanRequestUpdate = (): PlanUpdateRequestInterface => ({
  ...createBaseUpdate(),
  targetAccountId: "",
});

const createPlanPeriodUpdate = (): PlanUpdatePeriodRequestInterface => ({
  ...createBaseUpdate(),
});

const initialState: StompState = {
  subscriptionRequestUpdated: createRequestUpdate(),
  subscriptionActivatedUpdated: createBaseUpdate(),
  subscriptionConfirmUpdated: createBaseUpdate(),
  subscriptionGenerateUpdated: createBaseUpdate(),
  subscriptionRegisterUpdated: createBaseUpdate(),

  planAutoEnrollUpdated: createPlanRequestUpdate(),
  planPeriodUpdated: createPlanPeriodUpdate(),
  planStorageUpdated: createPlanRequestUpdate(),
};

const stompSubscriptionSlice = createSlice({
  name: "stompSubscription",
  initialState,
  reducers: {
    subscriptionUpdated: (
      state,
      action: PayloadAction<SubscriptionWsEntity>
    ) => {
      const { payload } = action;

      if (payload.status === SUBSCRIPTION_WS_STATUS.REQUESTED) {
        state.subscriptionRequestUpdated = {
          updatedDate: new Date(),
          accountId: payload.accountId,
          username: payload.username ?? "",
          installer: payload.installer ?? "",
          targetAccountId: payload.targetAccountId ?? "",
        };
        return;
      }

      const key = SUBSCRIPTION_WS_TO_STATE_KEY[payload.status] as Exclude<
        keyof StompState,
        | "subscriptionRequestUpdated"
        | "planAutoEnrollUpdated"
        | "planPeriodUpdated"
        | "planStorageUpdated"
      >;

      state[key] = {
        updatedDate: new Date(),
        accountId: payload.accountId,
      };
    },

    planUpdated: (state, action: PayloadAction<PlanWsEntity>) => {
      const { payload } = action;

      switch (payload.keyType) {
        case PLAN_KEY_TYPE.AUTO_ENROLL: {
          state.planAutoEnrollUpdated = {
            updatedDate: new Date(),
            accountId: payload.accountId,
            targetAccountId: payload.targetAccountId ?? "",
          };
          break;
        }

        case PLAN_KEY_TYPE.PERIOD: {
          state.planPeriodUpdated = {
            updatedDate: new Date(),
            accountId: payload.accountId,
            paymentCycle: payload.paymentCycle,
          };
          break;
        }

        case PLAN_KEY_TYPE.STORAGE: {
          state.planStorageUpdated = {
            updatedDate: new Date(),
            accountId: payload.accountId,
            targetAccountId: payload.targetAccountId ?? "",
          };
          break;
        }
      }
    },

    resetSubscriptionState: () => initialState,
  },
});

export const { subscriptionUpdated, resetSubscriptionState, planUpdated } =
  stompSubscriptionSlice.actions;
export const stompSubscriptionReducer = stompSubscriptionSlice.reducer;
